'use client';

import { useEffect, useState, useCallback, useRef, type ReactElement } from 'react';
import { useItemFetcher } from '@/hooks/use-item-fetcher';
import { useIsCartRoute } from '@/hooks/use-is-cart-route';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useTranslation } from 'react-i18next';
import { useBasketUpdater } from '@/providers/basket';
import { useCommerceFeedback } from '@/providers/commerce-feedback-context';
import { useDataLayer } from '@/hooks/use-data-layer';
import type { ShopperBasketsV2, ShopperSearch } from '@/scapi';
import type { ApproachingPromotion, EditorialPromotion, SCAPIShopperBasketsV2Schemas } from '@/types/scapi';
import type { BasketActionResponse } from '@/routes/types/action-responses';
import {
    filterApproachingPromotionsForProducts,
    filterApproachingPromotionsForSearchHit,
    filterEditorialPromotionsForProducts,
    filterEditorialPromotionsForSearchHit,
    type ProductWithPromotionRefs,
} from '@/lib/approaching-promotions-filter';
import { getPlpAddToCartProductId, getPlpProductToTrack } from '@/lib/cart/plp-basket-line';
import { buildDataLayerEcommerce, buildDataLayerItemSource } from '@/lib/analytics/data-layer-mappers';
import QuantitySelector from '@/components/quantity-selector';
import { ProductTileCustomQuickAddButton, type ProductTileCustomQuickAddDisplay } from './quick-add-button';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useAnalytics } from 'src/hooks/use-analytics';
import { getDisplayQuantity } from '@/lib/sold-by-weight';
import { cn } from '@/lib/utils';
import { useCommerceEnabled } from '@/hooks/use-commerce-enabled';

type SearchHit = ShopperSearch.schemas['ProductSearchHit'];

export function ProductTileGenericLineQuantity({
    line,
    product,
    selectedColorValue,
    productName,
    quantityLayout,
    disabled = false,
    className,
    max,
}: {
    line: NonNullable<SCAPIShopperBasketsV2Schemas['ProductItem']>;
    product: ProductWithPromotionRefs & Partial<Pick<SearchHit, 'variants'>>;
    selectedColorValue?: string | null;
    productName: string;
    quantityLayout: 'horizontal' | 'vertical';
    disabled?: boolean;
    className?: string;
    /** Caps quantity (e.g. choice-based bonus products). Falls back to cart config max. */
    max?: number;
}): ReactElement {
    const { t: tCommon } = useTranslation('common');
    const config = useConfig();
    const { showAddedToCart, showRemovedFromCart, showFailedToUpdateCart } = useCommerceFeedback();
    const isCartRoute = useIsCartRoute();
    const isDesktop = useMediaQuery('(min-width: 1024px)');
    const updateBasket = useBasketUpdater();
    const dataLayer = useDataLayer();
    const analytics = useAnalytics();
    const fetcher = useItemFetcher({
        itemId: line.itemId ?? undefined,
        componentName: 'product-tile-cart-quantity',
    });
    const pendingRemoveByTrashRef = useRef(false);
    const pendingRemovalRef = useRef<unknown>(null);
    const lastSubmittedQtyRef = useRef<number | null>(null);
    const pendingQtyRef = useRef<number | null>(null);
    const debounceTimerRef = useRef<number | null>(null);
    const prevLineItemIdRef = useRef(line.itemId);
    const previousQtyRef = useRef<number | null>(null);
    const lineQuantity = getDisplayQuantity(line);
    const [localQty, setLocalQty] = useState<number>(lineQuantity);

    const maxQuantity = max ?? config.pages?.cart?.maxQuantityPerItem ?? 999;
    const removeAction = config.pages?.cart?.removeAction ?? '/action/cart-item-remove';
    const finalProductName = productName || tCommon('productGeneric');
    const addedProductId = line.productId ?? getPlpAddToCartProductId(product as SearchHit, selectedColorValue);

    const filterToastApproachingPromotions = useCallback(
        (approachingPromotions: ApproachingPromotion[] | undefined) => {
            if (product.variants?.length) {
                return filterApproachingPromotionsForSearchHit(
                    approachingPromotions,
                    product as SearchHit,
                    addedProductId
                );
            }
            return filterApproachingPromotionsForProducts(approachingPromotions, [product]);
        },
        [addedProductId, product]
    );

    const filterToastEditorialPromotions = useCallback(
        (editorialPromotions: EditorialPromotion[] | undefined) => {
            if (product.variants?.length) {
                return filterEditorialPromotionsForSearchHit(editorialPromotions, product as SearchHit, addedProductId);
            }
            return filterEditorialPromotionsForProducts(editorialPromotions, [product]);
        },
        [addedProductId, product]
    );

    // Keep local UI in sync when the basket line changes externally.

    useEffect(() => {
        setLocalQty(lineQuantity);
        pendingQtyRef.current = null;
        if (debounceTimerRef.current != null) {
            window.clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        if (prevLineItemIdRef.current !== line.itemId) {
            lastSubmittedQtyRef.current = null;
            pendingRemoveByTrashRef.current = false;
            pendingRemovalRef.current = null;
            prevLineItemIdRef.current = line.itemId;
        }
    }, [line.itemId, lineQuantity]);

    const flushPending = useCallback(() => {
        if (!line.itemId) return;
        if (disabled || fetcher.state === 'submitting') return;
        const next = pendingQtyRef.current;
        if (next == null) return;
        pendingQtyRef.current = null;
        lastSubmittedQtyRef.current = next;
        previousQtyRef.current = lineQuantity;

        if (next < 1) {
            pendingRemoveByTrashRef.current = true;
            const removedQuantity = previousQtyRef.current ?? line.quantity ?? 1;
            pendingRemovalRef.current = buildDataLayerItemSource(
                { ...line, quantity: removedQuantity },
                getPlpProductToTrack(product as SearchHit, selectedColorValue, removedQuantity)
            );
            const form = new FormData();
            form.append('itemId', line.itemId);
            void fetcher.submit(form, { method: 'POST', action: removeAction });
            return;
        }

        pendingRemovalRef.current = null;
        pendingRemoveByTrashRef.current = false;
        const form = new FormData();
        form.append('itemId', line.itemId);
        form.append('quantity', String(next));
        void fetcher.submit(form, { method: 'PATCH', action: '/action/cart-item-update' });
    }, [disabled, fetcher, line, product, removeAction, selectedColorValue, lineQuantity]);

    const onQuantityChange = useCallback(
        (next: number) => {
            if (!line.itemId) return;

            // Optimistic UI: update immediately, but don't fire network yet.
            const clamped = next < 0 ? 0 : next;
            setLocalQty(clamped);
            pendingQtyRef.current = clamped;

            if (debounceTimerRef.current != null) {
                window.clearTimeout(debounceTimerRef.current);
            }
            debounceTimerRef.current = window.setTimeout(() => {
                debounceTimerRef.current = null;
                flushPending();
            }, 250);
        },
        [flushPending, line.itemId]
    );

    useEffect(() => {
        if (fetcher.state !== 'idle' || !fetcher.data) {
            return;
        }
        const data = fetcher.data as BasketActionResponse & { availableQuantity?: number };

        const wasRemove = pendingRemoveByTrashRef.current;
        const submittedQty = lastSubmittedQtyRef.current;
        const isFreshResponse = wasRemove || submittedQty !== null;

        pendingRemoveByTrashRef.current = false;
        lastSubmittedQtyRef.current = null;

        if (!isFreshResponse) {
            return;
        }

        if (data.success && data.basket) {
            updateBasket(data.basket);
            const basket = data.basket as SCAPIShopperBasketsV2Schemas['Basket'];
            const approachingPromotions = basket?.c_approachingPromotions;
            const editorialPromotions = basket?.c_editorialPromotions;
            const isRemoveOutcome = wasRemove || submittedQty === 0;
            const isDecreasing =
                previousQtyRef.current !== null && submittedQty !== null && submittedQty < previousQtyRef.current;
            const isQuantityPatchOutcome = typeof submittedQty === 'number' && submittedQty > 0 && !wasRemove;

            if (isRemoveOutcome) {
                if (pendingRemovalRef.current) {
                    dataLayer.track({
                        event: 'remove_from_cart',
                        ecommerce: buildDataLayerEcommerce([pendingRemovalRef.current], {
                            currency: dataLayer.currency,
                            affiliation: dataLayer.affiliation,
                        }),
                        event_type: 'ga4_eec',
                    });
                    pendingRemovalRef.current = null;
                }
                if (!isCartRoute || isDesktop) {
                    showRemovedFromCart({ productName: finalProductName });
                }
            } else if (isQuantityPatchOutcome) {
                if (!isDecreasing) {
                    if (!isCartRoute || isDesktop) {
                        showAddedToCart({
                            productName: finalProductName,
                            approachingPromotions: isCartRoute
                                ? undefined
                                : filterToastApproachingPromotions(approachingPromotions),
                            editorialPromotions: isCartRoute
                                ? undefined
                                : filterToastEditorialPromotions(editorialPromotions),
                            messageKey: 'updatedCart',
                        });
                    }
                    const quantityDelta =
                        previousQtyRef.current !== null ? submittedQty - previousQtyRef.current : submittedQty;
                    if (quantityDelta > 0) {
                        const basketLine = basket.productItems?.find((item) => item.itemId === line.itemId);
                        // Keep the basket line untouched so its own quantity de-totalizes the unit
                        // price; report only the delta added this event via `eventQuantity`.
                        const cartItem = buildDataLayerItemSource(
                            basketLine ?? { productId: addedProductId, quantity: quantityDelta },
                            getPlpProductToTrack(product as SearchHit, selectedColorValue, quantityDelta)
                        );
                        void analytics.trackCartItemAdd({
                            cartItems: [cartItem as ShopperBasketsV2.schemas['ProductItem']],
                            eventQuantity: quantityDelta,
                        });
                    }
                } else {
                    if (!isCartRoute || isDesktop) {
                        showRemovedFromCart({
                            productName: finalProductName,
                            messageKey: 'updatedCart',
                        });
                    }
                    dataLayer.track({
                        event: 'remove_from_cart',
                        ecommerce: buildDataLayerEcommerce([pendingRemovalRef.current], {
                            currency: dataLayer.currency,
                            affiliation: dataLayer.affiliation,
                        }),
                        event_type: 'ga4_eec',
                    });
                }
            }
        } else if (data.success === false) {
            const availableQty = data.availableQuantity;
            if (
                typeof availableQty === 'number' &&
                Number.isFinite(availableQty) &&
                availableQty >= 0 &&
                submittedQty != null &&
                submittedQty > availableQty
            ) {
                setLocalQty(availableQty);
                pendingQtyRef.current = availableQty;
                flushPending();
                return;
            }

            showFailedToUpdateCart();
            pendingRemovalRef.current = null;
            setLocalQty(lineQuantity);
        }

        flushPending();
    }, [
        fetcher.data,
        fetcher.state,
        dataLayer,
        flushPending,
        addedProductId,
        isDesktop,
        isCartRoute,
        lineQuantity,
        filterToastApproachingPromotions,
        filterToastEditorialPromotions,
        selectedColorValue,
        product,
        showAddedToCart,
        showRemovedFromCart,
        showFailedToUpdateCart,
        finalProductName,
        updateBasket,
        analytics,
        line?.itemId,
    ]);

    return (
        <div className={cn('pointer-events-auto', className)} onClick={(e) => e.preventDefault()}>
            <div onClick={(e) => e.stopPropagation()} role="presentation">
                <QuantitySelector
                    value={Math.max(1, localQty)}
                    onChange={onQuantityChange}
                    layout={quantityLayout}
                    max={maxQuantity}
                    disabled={disabled || fetcher.state === 'submitting'}
                />
            </div>
        </div>
    );
}

export interface ProductTileCartOrQuickAddProps {
    product: SearchHit;
    productName: string;
    selectedColorValue?: string | null;
    display: ProductTileCustomQuickAddDisplay;
    className?: string;
    quantityLayout: 'horizontal' | 'vertical';
    /** Resolved in parent via {@link useProductTileBasketLine} (one lookup per tile). */
    basketLine: SCAPIShopperBasketsV2Schemas['ProductItem'] | undefined;
    /** Disable cart CTA/quantity controls until basket is hydrated. */
    disabled?: boolean;
    productUrl: string;
}

/**
 * Renders the Library quantity control when a matching basket line exists; otherwise the quick-add entry.
 * Skips the basket line lookup when the cart snapshot has no items (no extra work).
 */
export function ProductTileCartOrQuickAdd({
    product,
    productName,
    selectedColorValue,
    display,
    className,
    quantityLayout,
    basketLine: line,
    disabled = false,
    productUrl,
}: ProductTileCartOrQuickAddProps): ReactElement | null {
    const commerceEnabled = useCommerceEnabled();

    if (!commerceEnabled) {
        return null;
    }

    if (line?.itemId && getDisplayQuantity(line, 0) >= 1) {
        return (
            <ProductTileGenericLineQuantity
                line={line}
                product={product}
                selectedColorValue={selectedColorValue}
                productName={productName}
                quantityLayout={quantityLayout}
                disabled={disabled}
                className={className}
            />
        );
    }

    return (
        <ProductTileCustomQuickAddButton
            product={product}
            productName={productName}
            selectedColorValue={selectedColorValue}
            display={display}
            className={className}
            disabled={disabled}
            productUrl={productUrl}
        />
    );
}
